<template>
  <bm-text
    v-model="internalValueString"
    v-bind="$attrs"
    prepend-icon="access_time"
    :error-messages="errors"
    :clearable="clearable"
    @blur="blurEvent"
    @input="validateTime"
  />
</template>

<script lang="ts">
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { LocalTime } from "@js-joda/core";
import { ChronoUnit } from "@js-joda/core";
import type { PropType } from "vue";
import Vue, { computed, defineComponent, ref, watch } from "vue";
import { useI18n } from "@/plugins/i18n";

export default defineComponent({
  name: "BmTime",
  props: {
    value: {
      type: Object as PropType<LocalTime | null>,
      required: false,
    },
    clearable: {
      type: Boolean,
      required: false,
    },
    errorMessages: {
      type: Array as PropType<string[]>,
      required: false,
      default: () => [],
    },
  },

  model: {
    prop: "value",
    event: "input",
  },
  setup(props, context) {
    const internalValueString = ref("");
    const errors = ref<string[]>([]);
    const { $m } = useI18n();

    watch(
      () => props.value,
      (newValue) => {
        internalValueString.value = newValue
          ? newValue.truncatedTo(ChronoUnit.MINUTES).toString()
          : "";
      },
      { immediate: true }
    );

    const blurEvent = () => {
      const parsedTime = tenantService.parseFormattedTime(
        internalValueString.value
      );

      // Emit if valid time entered or time has been cleared
      if ((props.clearable && !internalValueString.value) || parsedTime) {
        context.emit("input", parsedTime);
      }

      Vue.nextTick(() => {
        if (parsedTime) {
          internalValueString.value = parsedTime.toString();
        } else {
          // Clear internal value if clearable, otherwise revert
          if (props.clearable && internalValueString.value === "") {
            internalValueString.value = "";
          } else {
            internalValueString.value = props.value
              ? props.value.toString()
              : "";
            errors.value = [];
          }
        }
      });
    };

    const validateTime = () => {
      const parsedTime = tenantService.parseFormattedTime(
        internalValueString.value
      );
      if ((props.clearable && !internalValueString.value) || parsedTime) {
        errors.value = [];
      } else {
        errors.value = [$m.validation_invalid_time];
      }
    };

    const allErrorMessages = computed(() => [
      ...errors.value,
      ...props.errorMessages,
    ]);
    return {
      internalValueString,
      blurEvent,
      validateTime,
      errors: allErrorMessages,
    };
  },
});
</script>
